// @ts-strict-ignore
import {Directive, EventEmitter, inject, Injector, ViewChild} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {
	GeneratedTypePredicates,
	NotificationService,
	SimChecklistService,
	SimConfigurationService,
	SimEventService,
	SimExportService,
	SimFavoriteService,
	SimMaterialDialogService,
	SimRecorderService,
	SimRecordingsService,
	SimSessionService,
	SimSystemExtendedService,
	SimUserExtendedService,
	SimUtilityService,
	SystemHub,
} from "@services";
import {
	AudioChannelState,
	CameraViewMode,
	Event,
	Marker,
	Modules,
	ParticipantType,
	ReasonForCannotDeleteRecording,
	Scenario,
	ScenarioRecording,
	SimUser,
	TrainingSession,
	UserClientTypeView,
} from "@models";
import {TranslateService} from "@ngx-translate/core";
import {Router} from "@angular/router";
import Enumerable from "linq";
import {Translations, TranslationToken} from "@translations";
import {MatSliderDragEvent} from "@angular/material/slider";
import {PanelComponent} from "@common/components/panels/panel/panel.component";

export class TimeProperties {
	position: number;
	totalMilliseconds: number;
	scenarioRecordingSceneIndex: number;
	paused: boolean;

	constructor() {
		this.reset();
	}

	reset() {
		this.position = 0;
		this.totalMilliseconds = 0;
		this.scenarioRecordingSceneIndex = 0;
		this.paused = true;
	}

	set(timeProperties: TimeProperties) {
		this.position = timeProperties.position;
		this.totalMilliseconds = timeProperties.totalMilliseconds;
		this.scenarioRecordingSceneIndex = timeProperties.scenarioRecordingSceneIndex;
		this.paused = timeProperties.paused;
	}
}

/**
 * Scenario Archive Base Component
 *
 * Basic Recording/Scenario Archive functionality
 * Defines basic recording archive features which will be adopted from the
 * child components
 */
@Directive()
export abstract class ScenarioArchiveBase {
	T = Translations;
	@ViewChild("appPanels", {static: false}) newPanels: PanelComponent;

	hasLicenseModule = SimConfigurationService.hasLicenseModule;
	Modules = Modules;

	public ParticipantType = ParticipantType;
	public CameraViewMode = CameraViewMode;

	protected _simSystemService: SimSystemExtendedService;
	protected _systemHub: SystemHub;
	protected _simRecordingService: SimRecordingsService;
	protected _simRecorderService: SimRecorderService;
	protected _simChecklistService: SimChecklistService;
	protected _simNotificationService: NotificationService;
	protected _simUserService: SimUserExtendedService;
	protected _emitterService: EventEmitterService;
	protected _simUtilityService: SimUtilityService;
	protected _simExportService: SimExportService;
	protected _simEventService: SimEventService;
	protected _simDialogService: SimMaterialDialogService;
	protected _simFavoriteService: SimFavoriteService;
	protected _simSessionService = inject(SimSessionService);

	protected _router: Router;
	public translate: TranslateService;

	/* ******************
	 * ATTRIBUTES		*
	 * -----------------*
	 * Component based	*
	 *******************/
	public user: SimUser; // User
	public scenarioRecording: ScenarioRecording; // Recording
	public scenario: Scenario; // Scenario
	public trainingSession: TrainingSession = null; // Training Session
	public event: Event = null;

	public = 1;
	public readonly timeProperties = new TimeProperties(); // Time Properties

	public info = {
		isPlaying: false,
		lastJumpToPosition: 0,
		playbackRate: 1,
	};

	public dragging = false; // True if seekbar dragging is active
	public lastStateIsPlaying: boolean = false;

	/*
	 * Route parameter
	 */
	public rootFolderIndex: number; // Root folder index
	public recordingId: string; // Recording id

	reloadExportsEmitter = new EventEmitter<void>();
	uniqueUserClientTypeView: UserClientTypeView[] = [];

	/**
	 * Constructor
	 */
	protected constructor(protected _injector: Injector) {
		// Inject used Services
		this._simSystemService = this._injector.get(SimSystemExtendedService);
		this._systemHub = this._injector.get(SystemHub);
		this._simRecordingService = this._injector.get(SimRecordingsService);
		this._simChecklistService = this._injector.get(SimChecklistService);
		this._simNotificationService = this._injector.get(NotificationService);
		this.translate = this._injector.get(TranslateService);
		this._simUserService = this._injector.get(SimUserExtendedService);
		this._emitterService = this._injector.get(EventEmitterService);
		this._router = this._injector.get(Router);
		this._simUtilityService = this._injector.get(SimUtilityService);
		this._simExportService = this._injector.get(SimExportService);
		this._simEventService = this._injector.get(SimEventService);
		this._simDialogService = this._injector.get(SimMaterialDialogService);
		this._simFavoriteService = this._injector.get(SimFavoriteService);
		this._simRecorderService = this._injector.get(SimRecorderService);
	}

	/**
	 * initialize
	 *
	 * initialize recording archive
	 * requires recordingId and rootFolderIndex
	 */
	protected async initialize() {
		// Set user / fetch user from UserService
		this.user = this._simUserService.user;

		// Initialization requires setting route-params (scenarioRecordingId and rootFolderIndex)
		//await this.createTabletInfo();
		await this.initializeData();
		this.initializeEventHandlers();
	}

	/**
	 * initializeEventHandlers
	 *
	 * register eventhandlers
	 */
	private initializeEventHandlers() {
		this._emitterService.onScenarioRecordingPlayerTimeUpdated.subscribe((args) => {
			this.timeProperties.set(args);
		});

		this._emitterService.onScenarioRecordingPlayerPlaying.subscribe(() => {
			this.info.isPlaying = true;
		});
		this._emitterService.onScenarioRecordingPlayerFinished.subscribe(() => {
			this.info.isPlaying = false;
		});
		this._emitterService.onScenarioRecordingPlayerStopped.subscribe(() => {
			this.info.isPlaying = false;
		});
		this._emitterService.onScenarioRecordingPlayerPaused.subscribe(() => {
			this.info.isPlaying = false;
		});

		this._emitterService.onScenarioRecordingChanged.subscribe(async (args) => {
			if (this.scenarioRecording.id === args.scenarioRecordingId) {
				this.scenarioRecording = await this._simRecordingService.getRecordingById(args.scenarioRecordingId);
				this.initializeAudiochannelStates();
				this.determineUsersTakenBookmarks();
			}
		});
	}

	/**
	 * openEditRecordingModel
	 */
	public openEditRecordingModel() {
		this._simDialogService
			.openChangeRecordingMetaDialog({
				eventName: this.event.name,
				sessionName: this.trainingSession.title,
				scenario: this.scenario,
				eventDescription: this.event.description,
				scenarioRecordingState: this.scenarioRecording.state,
			})
			.then(async (result) => {
				if (result.action === "ok") {
					await this._simRecorderService
						.updateRecordingMetaData({
							scenarioRecordingId: this.scenarioRecording.id,
							recordingName: null,
							scenarioId: result.scenario?.id,
							sessionTitle: result.sessionName,
							eventName: result.eventName,
							eventDescription: result.eventDescription,
						})
						.then(() => this.initializeData());
				}
			});
	}

	public async openParticipantDialog(participantType: ParticipantType) {
		if (GeneratedTypePredicates.isExamSession(this.trainingSession)) {
			const userId = this.scenarioRecording.participants.find((p) => p.participantType === participantType)?.userId;
			const preselectedUser = userId ? await this._simUserService.getUserById(userId) : null;

			this._simDialogService
				.openExamParticipantDialog({
					participantType,
					preselectedUser,
					examSession: this.trainingSession,
					scenarioRecording: this.scenarioRecording,
				})
				.then(async () => {
					this.trainingSession = (await this._simSessionService.getSessionById(this.trainingSession.id)) as TrainingSession;
					this.scenarioRecording = await this._simRecordingService.getRecordingById(this.scenarioRecording.id);
				});
		} else {
			this._simDialogService
				.openParticipantListDialog({
					trainingSession: this.trainingSession,
					isEditable: true,
					participantType: participantType,
					scenarioRecording: this.scenarioRecording,
					trainingRoomId: null,
				})
				.then();
		}
	}

	/**
	 * initializeRecording
	 */
	private async initializeRecording() {
		if (this.recordingId) {
			try {
				this.scenarioRecording = await this._simRecordingService.getRecordingById(this.recordingId);
				this.determineUsersTakenBookmarks();
			} catch (ex) {
				this._simNotificationService.notifyError(`Error loading recording: ${ex}`);
			}
		}
	}

	/*
	 * SEEKBAR METHODS
	 */

	public async sliderStartDrag($event: MatSliderDragEvent) {
		//console.debug($event);
		this.dragging = true;
		this.lastStateIsPlaying = !this.timeProperties.paused;
		this.pause();
	}

	public async sliderStopDrag($event: MatSliderDragEvent) {
		//console.debug($event);
		this.jumpTo($event.value);
		if (this.lastStateIsPlaying) {
			this.play();
		}
		this.dragging = false;
	}

	/*
	 * VIDEO METHODS
	 */

	/**
	 * play
	 *
	 * emits play signal to player
	 */
	public play() {
		if (this.info.isPlaying) {
			this.info.isPlaying = false;
			setTimeout(() => {
				this.info.isPlaying = true;
			});
		} else {
			this.info.isPlaying = true;
		}
	}

	/**
	 * pause
	 *
	 * Emits pause signal to player
	 */
	public pause() {
		this.info.isPlaying = false;
	}

	/**
	 * toggleFullScreen
	 *
	 * toggles Fullscsreen
	 */
	public toggleFullScreen() {
		$(".SIMStation_videos").toggleClass("toggleFullscreen");
	}

	/**
	 * jumptTo
	 *
	 * emits jumpto signal with params checkListItem
	 *
	 * @param answerTime
	 */
	public jumpToWithOffset(answerTime: number) {
		const jumpTimeInMs = SimConfigurationService.settings.markerTimeOffset * 1000;
		const positionMs = Math.max(0, answerTime + jumpTimeInMs);

		return this.jumpTo(positionMs);
	}

	public jumpToMarker(marker: Marker) {
		const jumpTimeInMs = SimConfigurationService.settings.markerTimeOffset * 1000;
		const positionMs = Math.max(0, marker.position + jumpTimeInMs);

		return this.jumpTo(positionMs);
	}

	protected jumpRelative(relativeMilliseconds: number) {
		const targetPosition: number = Math.min(
			this.timeProperties.totalMilliseconds,
			Math.max(0, this.timeProperties.position + relativeMilliseconds),
		);

		this.jumpTo(targetPosition);
	}

	/**
	 * jumpTo
	 *
	 * Emits jumpto Signal marker
	 *
	 * @param millisecond
	 * @param marker
	 */
	protected jumpTo(millisecond: number) {
		//SIMSOFT-7891 - Angular Change Detection needs to be able to detect it
		if (this.info.lastJumpToPosition == millisecond) {
			millisecond++;
		}
		this.info.lastJumpToPosition = millisecond;
	}

	/**
	 * DeleteRecording
	 *
	 * @param recordingId
	 */
	public deleteRecording(recordingId) {
		if (this.trainingSession && !this.trainingSession.isLockedForDeletion) {
			this._simDialogService
				.openDeleteDialog({
					deleteTitle: this.translate.instant(Translations.RECORDINGS_DELETE_DIALOG_TITLE),
					deleteText: this.translate.instant(Translations.RECORDINGS_DELETE_DIALOG_TEXT, {
						recordingName: this.scenarioRecording.name,
					}),
					deleteButtonText: this.translate.instant(Translations.RECORDINGS_DELETE_DIALOG_OK),
					cancelText: this.translate.instant(Translations.RECORDINGS_DELETE_DIALOG_CANCEL),
				})
				.then((data) => {
					if (data.action === "ok") {
						this._simRecordingService
							.deleteRecording({
								recordingId,
							})
							.then((response) => {
								if (response) {
									if (response.couldDelete) {
										this._simNotificationService.notifySuccess(
											this.translate.instant(this.T.SCENARIO_RECORDING_DELETE_SUCCESS),
										);

										this._router.navigate(["/web-manager/list/scenario-archive"]);
									} else {
										let reasonTranslationsKey: TranslationToken = this.T.SCENARIO_RECORDING_DELETE_FAILED_GENERAL;
										switch (response.reason) {
											case ReasonForCannotDeleteRecording.ExportInProgress:
												reasonTranslationsKey = this.T.SCENARIO_RECORDING_DELETE_FAILED_EXPORT_IN_PROGRESS;
										}
										this._simDialogService
											.openInformationDialog({
												okText: this.translate.instant(this.T.BUTTON_OK),
												title: this.translate.instant(this.T.SCENARIO_RECORDING_DELETE_FAILED_TITLE),
												description: this.translate.instant(reasonTranslationsKey),
											})
											.then();
									}
								} else {
									this._simNotificationService.notifyError(
										this.translate.instant(this.T.SCENARIO_RECORDING_DELETE_FAILED_GENERAL),
									);
								}
							});
					}
				});
		}
	}

	/**
	 * initializeData
	 *
	 * Loading recording
	 * Loading scenarioById
	 * Loading markersets
	 * Loading recordingGroup
	 */
	private async initializeData() {
		this.scenarioRecording = await this._simRecordingService.getRecordingById(this.recordingId);
		this.timeProperties.totalMilliseconds = this.scenarioRecording.recordingTimeMs;
		this.determineUsersTakenBookmarks();
		this.initializeAudiochannelStates();

		this.scenario = this.scenarioRecording.scenario;

		this.scenarioRecording["isFavorited"] = !!(await this._simFavoriteService.getScenarioRecordingFavorite(this.recordingId));

		// SIMSOFT-4883 ASturl 18.03.2020
		// Added trainingSession because deletion locking is stored there
		if (this.scenarioRecording && this.scenarioRecording.trainingSessionId) {
			const trainingSessionId = this.scenarioRecording.trainingSessionId;

			this.trainingSession = (await this._simSessionService.getSessionById(trainingSessionId)) as TrainingSession;
			this.event = await this._simEventService.getEventByTrainingSessionId(trainingSessionId);
		}
	}

	public backward(time: number) {
		let newTime = this.timeProperties.position - time;
		if (newTime < 0) {
			newTime = 0;
		}

		this.jumpTo(newTime);
	}

	public forward(time: number) {
		let newTime = this.timeProperties.position + time;
		if (newTime > this.timeProperties.totalMilliseconds) {
			newTime = this.timeProperties.totalMilliseconds;
		}

		this.jumpTo(newTime);
	}

	public toggleFavorite() {
		if (this.scenarioRecording) {
			this._simFavoriteService.toggleScenarioRecordingFavorite(this.scenarioRecording.id).then((isFavorited) => {
				this.scenarioRecording["isFavorited"] = isFavorited;
			});
		}
	}

	public isMultiChannelAudio() {
		return this.scenarioRecording?.isMultiChannelAudio;
	}

	public audioChannelStates: {[key: string]: AudioChannelState} = {};
	public audioChannelGroupStates: {[key: string]: AudioChannelState} = {};

	public initializeAudiochannelStates() {
		if (this.scenarioRecording) {
			if (!this.isMultiChannelAudio()) {
				let singleChannelId = SimConfigurationService.settings.singleChannelId;
				this.addDefaultIfNotExists(this.audioChannelStates, singleChannelId);
			} else {
				(this.scenarioRecording?.recordingScenes ?? []).forEach((scene) => {
					(scene?.audioFiles ?? []).forEach((audiofile) => {
						(audiofile?.audioInputChannels ?? []).forEach((channel) => {
							this.addDefaultIfNotExists(this.audioChannelStates, channel.id);
							if (channel.audioChannelGroupId) {
								this.addDefaultIfNotExists(this.audioChannelGroupStates, channel.audioChannelGroupId);
							}
						});
					});
				});
			}
		}
	}

	private addDefaultIfNotExists(dict: {[key: string]: AudioChannelState}, key: string) {
		let c = dict[key];
		if (!c) {
			dict[key] = c = <AudioChannelState>{
				muted: false,
				solo: false,
				previousMuted: false,
				volume: 100,
				actualVolume: 100,
			};
		}
		dict[key] = c;
	}

	private determineUsersTakenBookmarks() {
		if (this.scenarioRecording && this.scenarioRecording?.markers?.length > 0) {
			const userClientTypeView: UserClientTypeView[] = Enumerable.from(this.scenarioRecording.markers)
				.select((marker) => {
					return {
						simUserId: marker.userId,
						clientType: marker.clientType,
					};
				})
				.where((x) => !!x?.simUserId)
				.toArray();

			this.uniqueUserClientTypeView = [...new Map(userClientTypeView.map((item) => [item["simUserId"], item])).values()];
		}
	}
}
